<template>
  <div
    class="flex flex-row bg-primary rounded-lg text-white cursor-pointer p-2 ml-2 mt-5"
    style="width: 230px"
    @click="togglePaymentModal"
  >
    <vs-popup 
      class="holamundo" 
      title="Add payment method" 
      :active.sync="showAddPayment"
      @close="showAddPayment = false"
    >
      <div v-if="hasCreditCard || (hasBankAccount && !$isUSRegion()) || (payToEnabled && hasPayTo && $isAuRegion())">
        <vs-tabs>
          <vs-tab 
            v-if="hasCreditCard"
            label="Credit Card" 
            @click="paymentType = 'CreditCard'"
          >
            <stripe-elements
              ref="stripeElement"
              :title="saveCreditCard"
              :pk="stripePublishableKey"
              :amount="totalPrice"
              locale="en"
              @token="tokenCreated"
              @loading="loading = $event"
              @error="onStripeError"
            />
            <vs-button 
              @click="submit"
              :disabled="loading"
            >
              <span v-if="!loading">Save</span>
              <span v-else>Processing...</span>
            </vs-button>
          </vs-tab>
          
          <vs-tab
            v-if="hasBankAccount && !$isUSRegion()"
            label="Bank Account"
            @click="paymentType = 'ClinicBankAccount'"
          >
            <BankSetup
              :totalPrice="totalPrice"
              :financialDetails="financialDetails"
              :isNewClinicBankAccount="isNewClinicBankAccount"
              :shippingDetails="shippingDetails"
              :paymentEmail="paymentEmail"
              :from-list="true"
              :paymentType="'ClinicBankAccount'"
              :repayment="true"
              :buttonTitle="'Save'"
              :clinicId="clinicId"
              @onClinicBankPaymentSuccess="clinicBankPaymentSuccess"
            />
          </vs-tab>

          <!-- FRH-747: gated on clinicData.paymentTypes (hasPayTo), same as
          the two tabs above, alongside the LD flag — the flag remains the
          environment-wide rollout control, hasPayTo is the per-clinic
          entitlement an org admin opts a clinic into via
          ViewEditClinicSettings. -->
          <vs-tab
            v-if="payToEnabled && hasPayTo && $isAuRegion()"
            label="PayTo"
            @click="paymentType = 'PayTo'"
          >
            <div class="payto-tab-marker">
              <PayToSetup
                :key="showAddPayment"
                :clinicId="clinicId"
                :paymentEmail="paymentEmail"
                :buttonTitle="'Save'"
                @payto-setup-success="payToSetupSuccess"
                @error="onPayToError"
              />
            </div>
          </vs-tab>
        </vs-tabs>
      </div>
      <div v-else class="p-4 text-center">
        No payment methods available for this clinic
      </div>
    </vs-popup>
    <plus-icon size="1.5x" class="custom-class" />
    <div class="pl-4 pt-1">Add Payment Method</div>
  </div>
</template>

<script>
import BankSetup from '@/views/components/order-center/BankSetup.vue';
import PayToSetup from '@/views/components/order-center/PayToSetup.vue';
import StripeElements from '@/views/components/order-center/StripeElements.vue';
import { PlusIcon } from 'vue-feather-icons';
import { mapActions } from 'vuex';
import LDService from '@/services/launchdarkly';
import FEATURE_FLAGS from '@/constants/featureFlags';

export default {
  name: 'AddPaymentMethod',
  components: {
    PlusIcon,
    StripeElements,
    BankSetup,
    PayToSetup,
  },
  props: {
    totalPrice: {
      type: Number,
      required: true,
    },
    financialDetails: {
      type: Object,
      required: true,
    },
    isNewClinicBankAccount: {
      type: Boolean,
      required: true,
    },
    shippingDetails: {
      type: Object,
      required: true,
    },
    paymentEmail: {
      type: String,
      required: true,
    },
    clinicId: {
      type: String,
      required: true,
    },
    saveCreditCard: {
      type: String,
      default: 'Save Credit Card',
    },
  },
  data() {
    return {
      stripePublishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      showAddPayment: false,
      paymentType: '',
      loading: false,
      hasCreditCard: false,
      hasBankAccount: false,
      hasPayTo: false,
      // FRH-734: defaults to false (fail-closed) — a failed or slow flag
      // lookup must not show a payment option the clinic is not entitled to.
      payToEnabled: false,
    };
  },
  methods: {
    ...mapActions('clinic', ['fetchClinicsDetail']),

    togglePaymentModal() {
      this.showAddPayment = !this.showAddPayment;
      if (this.showAddPayment) {
        this.fetchClinicDetails();
        if (this.$isAuRegion()) this.fetchPayToFlag();
      }
    },

    async fetchClinicDetails() {
      try {
        const response = await this.fetchClinicsDetail(this.clinicId);
        const clinicDetails = response.data.data;
        this.hasCreditCard = (clinicDetails.paymentTypes && clinicDetails.paymentTypes.includes('creditCard')) || false;
        this.hasBankAccount = (clinicDetails.paymentTypes && clinicDetails.paymentTypes.includes('DebitCard')) || false;
        this.hasPayTo = (clinicDetails.paymentTypes && clinicDetails.paymentTypes.includes('PayTo')) || false;
      } catch (error) {
        this.hasCreditCard = false;
        this.hasBankAccount = false;
        this.hasPayTo = false;
      }
    },
    async fetchPayToFlag() {
      try {
        // FRH-734: race against a hard timeout rather than trusting getFlag's
        // own promise to settle — it has no reject path for a stuck LD client.
        const flagLookup = LDService.getFlag(FEATURE_FLAGS.PAYTO_PAYMENT_METHOD, false);
        const timeout = new Promise((resolve) => setTimeout(() => resolve(false), 5000));
        this.payToEnabled = await Promise.race([flagLookup, timeout]);
      } catch (error) {
        this.payToEnabled = false;
      }
    },
    payToSetupSuccess() {
      this.showAddPayment = false;
      this.$emit('payto-setup-success');
    },
    onPayToError(error) {
      this.$emit('error', error);
    },
    tokenCreated(token) {
      this.$emit('token-created', token);
      this.showAddPayment = false;
      this.$vs.notify({
        title: 'Success',
        text: 'Payment method processed',
        color: 'success',
      });
    },
    clinicBankPaymentSuccess() {
      this.showAddPayment = false;
      this.$emit('clinic-bank-payment-success');
    },
    submit() {
      this.$refs.stripeElement.submit();
    },
    onStripeError(error) {
      this.$emit('error', error);
      this.$vs.notify({
        title: 'Error',
        text: error.message || 'Failed to process payment',
        color: 'danger',
      });
    },
  },
  created() {
    // Resolve ahead of the modal being opened where possible — togglePaymentModal
    // also calls this, but starting it here shrinks the window where the tab
    // container falls back to the "No payment methods available" empty state
    // for a PayTo-only clinic while the flag lookup is still in flight.
    if (this.$isAuRegion()) this.fetchPayToFlag();
  },
};
</script>

<style scoped>
/*
 * Vuesax's vs-tabs measures and locks the active tab's content height for its
 * switch animation, then only remeasures on a tab click — not when content
 * changes size within the currently active tab. The PayTo tab does exactly
 * that (toggling "Use PayID instead" adds a field), so the wrapper's
 * overflow: hidden clips/overflows against a stale, too-short measurement.
 * Overriding it to grow with content trades away that switch animation for
 * never clipping or overflowing the popup — but .con-slot-tabs is the tab
 * content wrapper vs-tabs reuses for whichever tab is active, so a bare
 * override here would also strip Credit Card's and Bank Account's own
 * height lock/animation whenever either is the active tab. :has() scopes it
 * to only when PayTo's own marker is actually inside that wrapper.
 */
.holamundo >>> .con-slot-tabs:has(.payto-tab-marker) {
  height: auto !important;
  overflow: visible !important;
}
</style>

